import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TAAudioAgent, {
  RecommendationData,
  Review,
  ReviewData,
} from "ta-agent-sdk";
import cn from "classnames";
import { useTranslation } from "react-i18next";

export function SelectedRecommendation({
  agent,
  closeDetails,
  selectedRecommendation,
}: {
  agent: TAAudioAgent;
  closeDetails: () => void;
  selectedRecommendation: RecommendationData;
}) {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState<Partial<ReviewData>>({});
  const [translatedDescription, setTranslatedDescription] =
    useState<string>("");

  const fetchReviews = async (propertyId: number, language: string) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_BACKEND_HOST
            ? `https://${process.env.REACT_APP_BACKEND_HOST}`
            : "http://localhost:8000"
        }/reviews/${propertyId}?limit=1&language=${language}`,
      );
      setReviews(await response.json());
    } catch (error) {
      console.error("Failed to fetch reviews for recommendation:", error);
    }
  };

  const decodeHtmlEntities = (text: string): string => {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = text;
    return textArea.value;
  };

  const fetchTranslation = useCallback(
    async (text: string, language: string) => {
      try {
        const response = await fetch(
          `${
            process.env.REACT_APP_BACKEND_HOST
              ? `https://${process.env.REACT_APP_BACKEND_HOST}`
              : "http://localhost:8000"
          }/translate/?text=${encodeURIComponent(text)}&language=${language}`,
        );
        const data = await response.json();
        const translatedText = data.translated_text || text;
        setTranslatedDescription(decodeHtmlEntities(translatedText));
      } catch (error) {
        console.error("Failed to fetch translation:", error);
        setTranslatedDescription(text);
      }
    },
    [],
  );

  const details = useMemo(() => {
    const isLocation = "location" in selectedRecommendation;

    const name = isLocation
      ? selectedRecommendation.location.names.find((n) => n.primary === true)
          ?.value ||
        selectedRecommendation.location.names[0]?.value ||
        ""
      : selectedRecommendation.experience.names[0]?.value || "";
    const photos = selectedRecommendation?.photos?.photos;
    const rating = isLocation
      ? (selectedRecommendation.location.traveler_ratings?.overall.rating ??
        selectedRecommendation.location.overall_traveller_ratings
          ?.bubble_rating ??
        0)
      : selectedRecommendation.experience.overall_traveller_ratings
          .bubble_rating;
    const reviewCount = isLocation
      ? (selectedRecommendation.location.traveler_ratings?.overall.count ??
        selectedRecommendation.location.overall_traveller_ratings
          ?.total_review_count ??
        0)
      : selectedRecommendation.experience.overall_traveller_ratings
          .total_review_count;
    const description = isLocation
      ? selectedRecommendation.location.descriptions?.find(
          (x) => x.language === "en",
        )?.value
      : selectedRecommendation.experience.description.find(
          (x) => x.language === "en",
        )?.value;

    const baseDetails = {
      isLocation,
      name,
      photos,
      rating,
      reviewCount,
      description,
    };

    const currentLang = i18n.resolvedLanguage || i18n.language || "en";
    fetchReviews(
      isLocation
        ? selectedRecommendation.location.id
        : selectedRecommendation.experience.id,
      currentLang,
    );

    return {
      ...baseDetails,
      ...(isLocation
        ? {
            addresses: selectedRecommendation.location.addresses,
            phone: selectedRecommendation.location.phone_numbers?.[0]?.value,
            categories: selectedRecommendation.location.categories
              ?.map((cat) => cat.display_name)
              .join(", "),
            status: selectedRecommendation.location.status?.value,
            openingHours: selectedRecommendation.location.opening_hours,
            rankings: selectedRecommendation.location.rankings,
            awards: selectedRecommendation.location.awards,
            recommendedVisitLength:
              selectedRecommendation.location.recommended_visit_length,
            neighborhoods: selectedRecommendation.location.neighborhoods
              ?.map((n) => n.name)
              .join(", "),
            urls: selectedRecommendation.location.urls,
          }
        : {}),
    };
  }, [selectedRecommendation, i18n.language, i18n.resolvedLanguage]);

  const [photoIndex, setPhotoIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [sectionsOpen, setSectionsOpen] = useState({
    description: false,
    reviews: false,
    hours: false,
  });
  const toggleSection = (k: keyof typeof sectionsOpen) =>
    setSectionsOpen((s) => ({ ...s, [k]: !s[k] }));

  // Fetch translation when description or language changes
  useEffect(() => {
    if (details.description) {
      const currentLang = i18n.resolvedLanguage || i18n.language || "en";
      console.log("Description", details.description, currentLang);
      fetchTranslation(details.description, currentLang);
    }
  }, [
    details.description,
    i18n.resolvedLanguage,
    i18n.language,
    fetchTranslation,
  ]);

  // Reset carousel index and scroll position whenever the selection changes
  useEffect(() => {
    setPhotoIndex(0);
    if (carouselRef.current) {
      // Jump back to first image without animation
      carouselRef.current.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [selectedRecommendation]);

  const nextPhoto = () => {
    const total = (details as any)?.photos?.length || 0;
    if (total <= 1) return;
    const el = carouselRef.current;
    if (el) {
      const width = el.clientWidth;
      const next = Math.min(photoIndex + 1, total - 1);
      el.scrollTo({ left: next * width, behavior: "smooth" });
    } else {
      setPhotoIndex((i) => (i + 1) % total);
    }
  };

  const prevPhoto = () => {
    const total = (details as any)?.photos?.length || 0;
    if (total <= 1) return;
    const el = carouselRef.current;
    if (el) {
      const width = el.clientWidth;
      const prev = Math.max(photoIndex - 1, 0);
      el.scrollTo({ left: prev * width, behavior: "smooth" });
    } else {
      setPhotoIndex((i) => (i - 1 + total) % total);
    }
  };

  // Keep photoIndex in sync with scroll position for snap carousel
  const handleScroll = () => {
    const el = carouselRef.current;
    const total = (details as any)?.photos?.length || 0;
    if (!el || total <= 0) return;
    const width = el.clientWidth || 1;
    const idx = Math.round(el.scrollLeft / width);
    if (idx !== photoIndex)
      setPhotoIndex(Math.min(Math.max(idx, 0), total - 1));
  };

  const mapsLink = useMemo(() => {
    if (!details.addresses || details.addresses.length === 0) return null;
    return (
      <div className="flex items-center gap-2">
        <img
          src="/icons/pin.svg"
          alt={t("selectedRecommendation.locationAlt")}
          className="w-4 h-4"
        />
        <span>
          {details.addresses
            ? details.addresses[0].formatted
            : t("selectedRecommendation.openInMaps")}
        </span>
      </div>
    );
  }, [details, t]);

  const openingHours = useMemo(() => {
    const raw: any[] = (details as any).openingHours?.periods || [];
    const dayOrder = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const grouped: Record<string, string[]> = {};
    raw.forEach((p) => {
      if (!p?.day_of_week) return;
      const day =
        p.day_of_week.charAt(0).toUpperCase() +
        p.day_of_week.slice(1).toLowerCase();
      const range = `${p.opens} - ${p.closes}`;
      if (!grouped[day]) grouped[day] = [];
      if (!grouped[day].includes(range)) grouped[day].push(range);
    });
    const ordered = dayOrder.filter((d) => grouped[d] && grouped[d].length);
    return (
      <>
        {ordered.map((day) => (
          <div key={day} className="flex flex-1 gap-1">
            <span className="font-medium capitalize flex-1  font-trip-sans-medium">
              {day}
            </span>
            <div className="ml-2 flex flex-col gap-1">
              {grouped[day].map((range, index) => (
                <div key={index}>{range}</div>
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }, [details]);
  return (
    <aside className="h-full w-full fixed top-0 left-0 md:relative max-w-none md:w-[420px] md:max-w-[90vw] bg-white z-[100] md:z-[102] shadow-xl flex flex-col">
      {/* Scrollable content */}
      <div className="overflow-auto relative flex-1 pb-[140px] md:pb-0">
        {/* Hero image */}

        <button
          className="fixed top-2 right-2 rounded-full w-8 h-8 bg-white shadow-md text-white flex items-center justify-center transition-colors z-[200] "
          onClick={closeDetails}
          aria-label={t("selectedRecommendation.closeAria")}
        >
          <img
            src="/icons/close.svg"
            alt={t("common.close")}
            className="w-4 h-4"
          />
        </button>
        {details.photos && details.photos.length > 0 && (
          <div className="relative group">
            <div
              ref={carouselRef}
              onScroll={handleScroll}
              className="w-full h-[300px] md:h-56 overflow-x-auto flex snap-x snap-mandatory scroll-smooth scrollbar-hidden"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {details.photos.map((p: any, idx: number) => (
                <img
                  key={idx}
                  src={(p?.StandardSizeURL?.url as string) || ""}
                  alt={`${details.name} (${idx + 1}/${details.photos?.length})`}
                  className="w-full h-[300px] md:h-56 object-cover flex-shrink-0 min-w-full snap-center"
                  loading={idx === 0 ? "eager" : "lazy"}
                />
              ))}
            </div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/20 to-transparent" />

            {details.photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevPhoto}
                  aria-label={t("selectedRecommendation.previousPhoto")}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-gray-900 rounded-full p-2 shadow transition-opacity opacity-100"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={nextPhoto}
                  aria-label={t("selectedRecommendation.nextPhoto")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white text-gray-900 rounded-full p-2 shadow transition-opacity opacity-100"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs rounded-full px-2 py-1">
                  {photoIndex + 1}/{details.photos.length}
                </div>
              </>
            )}
          </div>
        )}
        <div className="flex-1 px-5 pt-5">
          <h2 className="text-2xl font-trip-sans-bold text-black leading-tight">
            {details.name}
          </h2>
        </div>

        <div className="p-5 space-y-6">
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <span className="mr-1 text-sm text-neutral-500">
                {details.rating.toFixed(1)}
              </span>
              <div className="flex gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full ${
                      i < details.rating ? "bg-[#284B35]" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-neutral-500 text-sm">
                {t("common.reviews", { count: details.reviewCount })}
              </span>
            </div>
          </div>
          {/* 1. Description (collapsible) */}
          {details.description && (
            <div className="text-sm text-gray-700">
              {translatedDescription || details.description}
            </div>
          )}

          {reviews && reviews.data && reviews.data.length > 0 && (
            <>
              <h3 className="text-lg font-trip-sans-bold text-black">
                {t("selectedRecommendation.travelersHeading")}
              </h3>
              <div
                className={cn(
                  "relative rounded-xl shadow-lg shadow-neutral-500/50 p-4 overflow-hidden flex flex-col gap-4",
                  {
                    "h-[150px]": !sectionsOpen.reviews,
                  },
                )}
              >
                {reviews.data.map((review: Review, idx: number) => (
                  <div key={idx} className="border-b border-gray-200 pb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-sm font-semibold text-gray-600">
                        {review.user?.username?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-gray-900 mb-1">
                          {review.user?.username || t("common.anonymous")}
                        </div>
                        <div className="flex items-center mb-2">
                          <div className="flex gap-1 mr-2">
                            {Array.from({ length: 5 }, (_, i) => (
                              <div
                                key={i}
                                className={`w-3 h-3 rounded-full ${
                                  i < review.rating
                                    ? "bg-[#284B35]"
                                    : "bg-gray-300"
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-neutral-500">
                            {review.rating.toFixed(1)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">
                          {review.text}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => toggleSection("reviews")}
                  className={`${
                    !sectionsOpen.reviews
                      ? "absolute bottom-0 left-0 right-0 "
                      : ""
                  } w-full py-4 font-trip-sans-bold text-sm text-black`}
                  style={{
                    ...(!sectionsOpen.reviews && {
                      background:
                        "linear-gradient(0deg, rgb(255, 255, 255) 40%, rgba(255, 255, 255, 0) 100%)",
                    }),
                  }}
                >
                  {sectionsOpen.reviews
                    ? t("selectedRecommendation.readLess")
                    : t("selectedRecommendation.readMore")}
                </button>
              </div>
            </>
          )}

          {/* 3. At a Glance section */}
          {(details.phone ||
            details.addresses ||
            (details.isLocation && (details as any).openingHours?.periods)) && (
            <h3 className="text-lg font-trip-sans-bold text-black">
              {t("selectedRecommendation.atAGlance")}
            </h3>
          )}
          <div className="flex flex-wrap gap-3 text-sm">
            {mapsLink}

            {/* Phone */}
            {details.isLocation && (details as any).phone && (
              <a
                href={`tel:${(details as any).phone}`}
                className="flex items-center gap-2 underline"
              >
                <img
                  src="/icons/phone.svg"
                  alt={t("selectedRecommendation.phoneAlt")}
                  className="w-4 h-4"
                />
                <span>{(details as any).phone}</span>
              </a>
            )}

            {/* Menu link (if provided under urls.menu) */}
            {details.isLocation && (details as any).urls?.menu && (
              <a
                onClick={() => {
                  agent.stopAutoUnmute();
                  agent._stopSpeaker();
                }}
                href={(details as any).urls.menu}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 underline"
              >
                <img
                  src="/icons/menu.svg"
                  alt={t("selectedRecommendation.menuAlt")}
                  className="w-4 h-4"
                />
                <span>{t("selectedRecommendation.menu")}</span>
              </a>
            )}
          </div>
          {details.isLocation && (details as any).openingHours?.periods && (
            <div
              className={cn(
                "relative rounded-xl shadow-lg shadow-neutral-500/50 p-4 overflow-hidden",
                {
                  "h-[150px]": !sectionsOpen.hours,
                },
              )}
            >
              <span className="text-lg font-trip-sans-bold text-gray-900 mb-2">
                {t("selectedRecommendation.openingHours")}
              </span>
              <div
                className={cn("flex flex-col w-full mt-2 gap-3 text-gray-700")}
              >
                {openingHours}
              </div>

              <button
                onClick={() => toggleSection("hours")}
                className={`${
                  !sectionsOpen.hours ? "absolute bottom-0 left-0 right-0 " : ""
                } w-full py-4 font-trip-sans-bold text-sm text-black`}
                style={{
                  ...(!sectionsOpen.hours && {
                    background:
                      "linear-gradient(0deg, rgb(255, 255, 255) 20%, rgba(255, 255, 255, 0) 100%)",
                  }),
                }}
              >
                {sectionsOpen.hours
                  ? t("selectedRecommendation.readLess")
                  : t("selectedRecommendation.readMore")}
              </button>
            </div>
          )}
        </div>
        {details.urls?.tripadvisor.main && (
          <a
            onClick={() => {
              agent.stopAutoUnmute();
              agent._stopSpeaker();
            }}
            href={details.urls.tripadvisor.main}
            className="rounded-full px-4 py-2 float-end mr-4 mb-4 font-trip-sans-bold text-sm bg-black !text-white hover:bg-gray-900"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("common.findOutMore")}
          </a>
        )}
      </div>
    </aside>
  );
}
